import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CURRENT_RELEASE_VERSION,
  filterReleaseNotesByAccess,
  findReleaseNote,
  isReleaseUnread,
  releaseNotes,
  shouldAutoOpenReleaseNotes,
  type ReleaseNote,
  type ReleaseNotesAccess,
} from '@/config/releaseNotes';
import { readLastSeenRelease, saveLastSeenRelease } from '@/services/releaseNotesPreferences';

interface UseReleaseNotesOptions {
  uid: string | null;
  ready: boolean;
  access: ReleaseNotesAccess;
}

export interface UseReleaseNotesResult {
  currentVersion: string;
  currentRelease: ReleaseNote | null;
  selectedRelease: ReleaseNote | null;
  visibleReleaseNotes: ReleaseNote[];
  isOpen: boolean;
  isCurrentReleaseUnread: boolean;
  openRelease: (version?: string) => void;
  closeReleaseNotes: () => void;
  markCurrentReleaseAsRead: () => void;
  startCurrentRelease: () => void;
}

export function useReleaseNotes({ uid, ready, access }: UseReleaseNotesOptions): UseReleaseNotesResult {
  const [lastSeenRelease, setLastSeenRelease] = useState<string | null>(null);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [openVersion, setOpenVersion] = useState<string | null>(null);
  const [sessionDismissedVersion, setSessionDismissedVersion] = useState<string | null>(null);
  const { isPlatformAdmin, isSuperAdmin, permissions } = access;

  const visibleReleaseNotes = useMemo(
    () => filterReleaseNotesByAccess(releaseNotes, { isPlatformAdmin, isSuperAdmin, permissions }),
    [isPlatformAdmin, isSuperAdmin, permissions]
  );
  const currentRelease = useMemo(
    () => findReleaseNote(CURRENT_RELEASE_VERSION, visibleReleaseNotes),
    [visibleReleaseNotes]
  );
  const selectedRelease = useMemo(
    () => visibleReleaseNotes.find((release) => release.version === openVersion) || currentRelease || visibleReleaseNotes[0] || null,
    [currentRelease, openVersion, visibleReleaseNotes]
  );

  useEffect(() => {
    setOpenVersion(null);
    setSessionDismissedVersion(null);
    setLastSeenRelease(null);
    setPreferencesLoaded(false);

    if (!ready || !uid) return;

    setLastSeenRelease(readLastSeenRelease(uid));
    setPreferencesLoaded(true);
  }, [ready, uid]);

  useEffect(() => {
    if (!ready || !uid || !preferencesLoaded || !currentRelease) return;
    if (shouldAutoOpenReleaseNotes(currentRelease.version, lastSeenRelease, sessionDismissedVersion)) {
      setOpenVersion(currentRelease.version);
    }
  }, [currentRelease, lastSeenRelease, preferencesLoaded, ready, sessionDismissedVersion, uid]);

  const openRelease = useCallback((version = CURRENT_RELEASE_VERSION) => {
    if (visibleReleaseNotes.some((release) => release.version === version)) {
      setOpenVersion(version);
    }
  }, [visibleReleaseNotes]);

  const closeReleaseNotes = useCallback(() => {
    if (openVersion === CURRENT_RELEASE_VERSION) {
      setSessionDismissedVersion(CURRENT_RELEASE_VERSION);
    }
    setOpenVersion(null);
  }, [openVersion]);

  const markCurrentReleaseAsRead = useCallback(() => {
    if (!currentRelease) return;

    if (uid) {
      saveLastSeenRelease(uid, currentRelease.version);
    }
    setLastSeenRelease(currentRelease.version);
    setSessionDismissedVersion(currentRelease.version);
    setOpenVersion(null);
  }, [currentRelease, uid]);

  const startCurrentRelease = useCallback(() => {
    closeReleaseNotes();
  }, [closeReleaseNotes]);

  return {
    currentVersion: CURRENT_RELEASE_VERSION,
    currentRelease,
    selectedRelease,
    visibleReleaseNotes,
    isOpen: openVersion !== null,
    isCurrentReleaseUnread: Boolean(
      ready && currentRelease && isReleaseUnread(currentRelease.version, lastSeenRelease)
    ),
    openRelease,
    closeReleaseNotes,
    markCurrentReleaseAsRead,
    startCurrentRelease,
  };
}
